import {Map} from "@luciad/ria/view/Map";
import React, {useEffect, useState} from "react";
import {ContextMenuContext, useContextMenuState} from "../hooks/useContextMenu";
import {MapContext} from "../hooks/useMapContext";
import {IToastContext, ToastContext, useToastState} from "../hooks/useToast";
import {isPromise} from "../util/Lang";
import {ContextMenu} from "./ContextMenu";
import {DefaultMapOverlays} from "./overlay/DefaultMapOverlays";
import {SampleMap} from "./SampleMap";
import "./SingleMapSample.css";
import {Toast} from "./Toast";

interface Props {
  /**
   * Called when the map has been initialized.
   * Use this to add layers etc.
   */
  onInit?: (map: Map, toastContext: IToastContext) => Promise<void> | void;
  /**
   * Include a default background imagery layer.
   */
  includeBackground?: boolean;
  /**
   * Include a default elevation layer.
   */
  includeElevation?: boolean;
  /**
   * Called when the map has been destroyed.
   * Use this to cleanup listeners etc.
   */
  onDestroy?: (map: Map) => void;
  /**
   * If specified, the map's reference will always be set to the specified value.
   * If not specified, the map's reference will be a reference specified in the URL,
   * e.g. http://localhost:3000/?webgl=true&reference=EPSG:3857 for a Web Mercator map.
   * If the URL also doesn't specify a reference, <code>defaultReference</code> will be used.
   */
  reference?: string;
  /**
   * The map's default reference if no reference is specified in the URL (cf. the <code>reference</code> prop).
   */
  defaultReference?: string;
  /**
   * Indicates whether a WebGLMap is created.
   */
  webgl?: boolean;
  /**
   * UI to be displayed in a bar below the map
   */
  bottomBar?: React.ReactNode;
  /**
   * UI to be displayed as an overlay over the map
   */
  mapOverlay?: React.ReactNode | null;
}

export const SingleMapSample = ({
                                  onInit,
                                  includeBackground,
                                  includeElevation,
                                  onDestroy,
                                  reference,
                                  defaultReference,
                                  webgl,
                                  mapOverlay,
                                  bottomBar
                                }: Props) => {
  const [map, setMap] = useState<Map | null>(null);
  const contextMenuState = useContextMenuState();
  const toastState = useToastState();

  useEffect(() => {
    if (map) {
      map.onShowContextMenu = contextMenuState.showContextMenu;
    }
  }, [map]);

  const handleError = (e: Error) => {
    toastState.showErrorToast(e.message, e);
  }

  const onInitWrapper = (map: Map): void => {
    if (onInit) {
      try {
        const initResult = onInit(map, toastState);
        if (isPromise(initResult)) {
          initResult.catch(handleError);
        }
      } catch (e) {
        handleError(e);
      }
    }
    setMap(map);
  }

  const onDestroyWrapper = (map: Map): void => {
    if (onDestroy) {
      try {
        onDestroy(map);
      } catch (e) {
        handleError(e);
      }
    }
    setMap(null);
  }

  return (
      <div className="single-map-sample">
        <ContextMenuContext.Provider value={contextMenuState}>
          <ToastContext.Provider value={toastState}>
            <SampleMap
                onInit={onInitWrapper}
                includeBackground={includeBackground}
                includeElevation={includeElevation}
                onDestroy={onDestroyWrapper}
                reference={reference}
                defaultReference={defaultReference}
                webgl={webgl}
            >
              {map && (mapOverlay === null ? null : (mapOverlay ?? <DefaultMapOverlays/>))}
            </SampleMap>
            {map && bottomBar &&
             <MapContext.Provider value={{map}}>
               <div className="bottom-bar">
                 {bottomBar}
               </div>
             </MapContext.Provider>
            }
            <ContextMenu/>
            <Toast/>
          </ToastContext.Provider>
        </ContextMenuContext.Provider>
      </div>
  )
}